import {
    AgenticHubAction,
    AgenticHubPayload,
    AH_HelpRequestedPayload,
    AH_ReadReceiptsUpdatedPayload,
    AH_SessionMessagePayload,
    AH_SessionStartedPayload,
    AH_TakeoverChangedPayload,
} from '@numeo/types'
import { AppDispatch, RootState } from 'store'
import { Category, INotification } from '../../../hooks/useNotification'
import { upsertNotification } from '../../../store/notificationSlice'
import { fetchSessionMessages } from '../services/dispatchHelpApi'
import {
    bumpUnread,
    ingestLiveMessage,
    mergeMessages,
    setAgentActivity,
    setHelpRequested,
    setReadMark,
    setTakeover,
    touchActivity,
    upsertSession,
} from './dispatchHelpSlice'

// The dispatch-help events. Anything else on the AgenticHub lane belongs to the AI Hub
// handler and is ignored here (both handlers run for every AgenticHub message).
const DISPATCH_HELP_ACTIONS = new Set<string>([
    AgenticHubAction.AH_SessionStarted,
    AgenticHubAction.AH_SessionMessage,
    AgenticHubAction.AH_TakeoverChanged,
    AgenticHubAction.AH_HelpRequested,
    AgenticHubAction.AH_ReadReceiptsUpdated,
    // The agent's search/scoring beats, mirrored to operators — without this the case below is
    // unreachable and the working indicator can never advance past its opening state.
    AgenticHubAction.AH_SearchPipelineStatus,
])

// Bounded dedupe of the envelope-level eventId (socket-service replays on reconnect).
const processedEventIds = new Set<string>()
const MAX_TRACKED = 500
function alreadyProcessed(eventId?: string): boolean {
    if (!eventId) return false
    if (processedEventIds.has(eventId)) return true
    processedEventIds.add(eventId)
    if (processedEventIds.size > MAX_TRACKED) {
        const oldest = processedEventIds.values().next().value
        if (oldest) processedEventIds.delete(oldest)
    }
    return false
}

// We don't stream AI chunks to dispatchers, so after a turn that yields an AI reply (a mobile
// user's message, or a dispatcher's @numeoai prompt) we refetch the active thread to pull in the
// reply. Staggered so both fast and slower agent turns are caught without needing the stream.
// Windows extend past a typical slow load-search AI turn (search + scoring can exceed 10s).
const REFETCH_DELAYS_MS = [1500, 5000, 12000, 25000]
const refetchTimers = new Map<string, ReturnType<typeof setTimeout>[]>()
export function scheduleReplyRefetch(tabId: string, dispatch: AppDispatch): void {
    refetchTimers.get(tabId)?.forEach(clearTimeout)
    const timers: ReturnType<typeof setTimeout>[] = REFETCH_DELAYS_MS.map((delay, i) =>
        setTimeout(() => {
            // Merge (not replace) so an out-of-order or slow fetch can't clobber newer live rows.
            void fetchSessionMessages(tabId)
                .then(({ messages }) => dispatch(mergeMessages({ tabId, messages })))
                .catch(() => {})
            // Last timer of THIS batch fired — release the map entry (unless a newer batch replaced it).
            if (i === REFETCH_DELAYS_MS.length - 1 && refetchTimers.get(tabId) === timers) refetchTimers.delete(tabId)
        }, delay)
    )
    refetchTimers.set(tabId, timers)
}

/** Cancel every pending reply-refetch — call on Dispatch Help unmount so timers don't keep firing
 *  fetches (up to 25s) and churning the store for a thread the operator has navigated away from. */
export function clearAllReplyRefetch(): void {
    refetchTimers.forEach((timers) => timers.forEach(clearTimeout))
    refetchTimers.clear()
}

export function handleDispatchHelpSocketEvent(dispatch: AppDispatch, payload: AgenticHubPayload, eventId?: string, getState?: () => RootState): void {
    if (!DISPATCH_HELP_ACTIONS.has(payload.action)) return
    if (alreadyProcessed(eventId)) return

    switch (payload.action) {
        case AgenticHubAction.AH_SessionStarted: {
            const p = payload as AH_SessionStartedPayload
            dispatch(upsertSession(p.session))
            const who = p.session.senderEmail || 'a mobile user'
            const notification: INotification = {
                id: `dispatch-session-${p.tabId}`,
                category: Category.System,
                message: `New AI dispatch session from ${who}`,
                secondaryMessage: p.preview,
                timestamp: Date.now(),
                type: 'info',
                path: '/dispatcher/dispatch-help',
                topic: 'permanent',
            }
            dispatch(upsertNotification(notification))
            break
        }
        case AgenticHubAction.AH_SessionMessage: {
            const p = payload as AH_SessionMessagePayload
            // Only materialize into an opened thread — unopened sessions just bump unread (see reducer).
            dispatch(ingestLiveMessage({ tabId: p.tabId, message: p.message }))
            dispatch(touchActivity({ tabId: p.tabId, lastActivityAt: p.lastActivityAt }))
            // Unread counts "someone may need me", so only a HUMAN turn bumps it. The agent answering a
            // driver is normal traffic; counting it would roughly triple the badge on conversations no
            // operator has to touch. Activity ordering still moves — the session genuinely did.
            if (p.message.role !== 'agent') dispatch(bumpUnread(p.tabId))
            // Driver tagged @dispatcher — they want a human (the AI was skipped). Alert loudly.
            if (p.wantsDispatcher) {
                const notification: INotification = {
                    id: `dispatch-help-wants-${p.tabId}`,
                    category: Category.System,
                    message: `Driver is asking for a dispatcher${p.senderEmail ? `: ${p.senderEmail}` : ''}`,
                    secondaryMessage: p.message.text,
                    timestamp: Date.now(),
                    type: 'warning',
                    path: '/dispatcher/dispatch-help',
                    topic: 'permanent',
                }
                dispatch(upsertNotification(notification))
            }
            // A plain user turn is (usually) followed by an AI reply we won't see streamed — refetch
            // the open thread to surface it. @dispatcher turns get no AI reply, so skip the refetch.
            else if (p.message.role === 'user' && getState?.()?.dispatchHelp?.activeSessionId === p.tabId) {
                dispatch(setAgentActivity({ tabId: p.tabId, message: 'Working…' }))
                scheduleReplyRefetch(p.tabId, dispatch)
            }
            break
        }
        // The agent's search/scoring beats, mirrored from the driver's own stream. They drive the
        // operator's working indicator; nothing is persisted, so a missed beat costs an animation frame.
        case AgenticHubAction.AH_SearchPipelineStatus: {
            const p = payload as { tabId: string; message?: string; status?: string }
            if (p.tabId) dispatch(setAgentActivity({ tabId: p.tabId, message: p.status === 'scoring_completed' ? '' : (p.message ?? 'Working…') }))
            break
        }
        case AgenticHubAction.AH_TakeoverChanged: {
            const p = payload as AH_TakeoverChangedPayload
            dispatch(setTakeover({ tabId: p.tabId, takeover: p.active ? (p.takeover ?? null) : null }))
            break
        }
        case AgenticHubAction.AH_HelpRequested: {
            const p = payload as AH_HelpRequestedPayload
            if (p.active) {
                // Make sure the session is in the list (it may be one we haven't loaded), flag the
                // raised hand, and alert the operator.
                if (p.session) dispatch(upsertSession(p.session))
                dispatch(setHelpRequested({ tabId: p.tabId, helpRequested: true }))
                const notification: INotification = {
                    id: `dispatch-help-request-${p.tabId}`,
                    category: Category.System,
                    message: `Driver requested a dispatcher${p.session?.senderEmail ? `: ${p.session.senderEmail}` : ''}`,
                    timestamp: Date.now(),
                    type: 'warning',
                    path: '/dispatcher/dispatch-help',
                    topic: 'permanent',
                }
                dispatch(upsertNotification(notification))
            } else {
                dispatch(setHelpRequested({ tabId: p.tabId, helpRequested: false }))
            }
            break
        }
        case AgenticHubAction.AH_ReadReceiptsUpdated: {
            const p = payload as AH_ReadReceiptsUpdatedPayload
            dispatch(setReadMark({ tabId: p.tabId, lastReadAt: p.lastReadAt }))
            break
        }
        default:
            break
    }
}
